import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { SkeletonList } from "../components/Skeleton";
import { Sparkline } from "../components/Sparkline";
import type { MatchRecord, WrestlerStats } from "../api/types";

function fmtDate(iso: string): string {
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const WIN_TYPE_LABEL: Record<string, string> = {
  FALL: "Fall",
  DEC: "Dec",
  MD: "Maj",
  TF: "TF",
  FOR: "FFT",
  DQ: "DQ",
  INJ: "Inj",
};

/** Derived record + standings stats from a match list. */
function computeStats(matches: MatchRecord[]) {
  let wins = 0,
    losses = 0,
    draws = 0,
    pins = 0,
    techs = 0,
    majors = 0,
    decisions = 0;
  for (const m of matches) {
    if (m.result === "W") wins++;
    else if (m.result === "L") losses++;
    else draws++;
    if (m.result === "W") {
      if (m.winType === "FALL") pins++;
      else if (m.winType === "TF") techs++;
      else if (m.winType === "MD") majors++;
      else if (m.winType === "DEC") decisions++;
    }
  }
  const total = wins + losses + draws;
  const winPct = total ? Math.round((wins / total) * 100) : 0;
  // Current WAR rating = most recent match's warAfter (matches sorted desc).
  const withWar = matches.filter((m) => typeof m.warAfter === "number");
  const currentWar = withWar[0]?.warAfter;
  const oldestWar = withWar[withWar.length - 1]?.warAfter;
  const warTrend =
    currentWar !== undefined && oldestWar !== undefined
      ? currentWar - oldestWar
      : undefined;
  return { wins, losses, draws, total, winPct, pins, techs, majors, decisions, currentWar, warTrend };
}

export function WrestlerRecord() {
  const wrestlers = useAsync(() => api.listWrestlers(), []);
  const [params, setParams] = useSearchParams();
  const [wrestlerId, setWrestlerId] = useState("");

  // Default to the ?w= param or the first wrestler once loaded.
  useEffect(() => {
    if (wrestlerId) return;
    const fromUrl = params.get("w");
    const first = wrestlers.data?.items[0]?.wrestlerId;
    const pick = fromUrl || first;
    if (pick) setWrestlerId(pick);
  }, [wrestlers.data, params, wrestlerId]);

  const matches = useAsync(
    () => (wrestlerId ? api.getWrestlerMatches(wrestlerId) : Promise.resolve({ items: [] })),
    [wrestlerId],
  );
  const scouting = useAsync(
    () =>
      wrestlerId
        ? api.getWrestlerScouting(wrestlerId)
        : Promise.resolve(null),
    [wrestlerId],
  );

  const [style, setStyle] = useState("");
  const [season, setSeason] = useState("");
  const [resultFilter, setResultFilter] = useState("");

  // Sort newest-first and apply filters.
  const all = useMemo(() => {
    const items = [...(matches.data?.items ?? [])];
    items.sort((a, b) => b.eventDate.localeCompare(a.eventDate));
    return items;
  }, [matches.data]);

  const styles = useMemo(
    () => Array.from(new Set(all.map((m) => m.style).filter(Boolean))) as string[],
    [all],
  );
  const seasons = useMemo(
    () => Array.from(new Set(all.map((m) => m.eventDate.slice(0, 4)))).sort().reverse(),
    [all],
  );

  const filtered = useMemo(
    () =>
      all.filter(
        (m) =>
          (!style || m.style === style) &&
          (!season || m.eventDate.startsWith(season)) &&
          (!resultFilter || m.result === resultFilter),
      ),
    [all, style, season, resultFilter],
  );

  const stats = useMemo(() => computeStats(filtered), [filtered]);
  const selected = wrestlers.data?.items.find((w) => w.wrestlerId === wrestlerId);

  // WAR rating over time (chronological) for the sparkline.
  const warSeries = useMemo(() => {
    return [...all]
      .filter((m) => typeof m.warAfter === "number")
      .sort((a, b) => a.eventDate.localeCompare(b.eventDate))
      .map((m) => m.warAfter as number);
  }, [all]);

  // Provider career stats (e.g. Wrestling Reimagined WAR Zone summary).
  const providerStats: WrestlerStats | undefined = useMemo(() => {
    const list = matches.data?.stats ?? [];
    return list.find((s) => s.provider === "reimagined") ?? list[0];
  }, [matches.data]);

  return (
    <div>
      <h1>Wrestler record</h1>
      <p className="muted">
        Your wrestlers' records, standings, and full match history — including
        results pulled from FloWrestling and Wrestling Reimagined.
      </p>

      {/* Wrestler picker */}
      <div className="card">
        <label htmlFor="w">Wrestler</label>
        <select
          id="w"
          style={{ width: "auto" }}
          value={wrestlerId}
          onChange={(e) => {
            setWrestlerId(e.target.value);
            params.set("w", e.target.value);
            setParams(params, { replace: true });
          }}
        >
          {wrestlers.data?.items.map((w) => (
            <option key={w.wrestlerId} value={w.wrestlerId}>
              {w.displayName}
              {w.club ? ` · ${w.club}` : ""}
            </option>
          ))}
        </select>
        {wrestlers.data && wrestlers.data.items.length === 0 && (
          <p className="muted" style={{ marginTop: "0.5rem" }}>
            No wrestlers yet. Add your wrestlers in your <a href="/profile">profile</a>.
          </p>
        )}
      </div>

      {matches.loading && <SkeletonList count={3} variant="card" />}
      {matches.error && <p className="error">{matches.error}</p>}

      {wrestlerId && matches.data && (
        <>
          {/* Record / standings summary */}
          <div className="card record-summary">
            <div className="record-headline">
              <span className="record-wl">
                {stats.wins}<span className="muted">–</span>{stats.losses}
                {stats.draws ? <span className="muted">–{stats.draws}</span> : null}
              </span>
              <span className="muted">
                {stats.total} match{stats.total === 1 ? "" : "es"} · {stats.winPct}% win rate
              </span>
            </div>
            <div className="record-stats">
              <div className="stat"><b>{stats.pins}</b><span>Pins</span></div>
              <div className="stat"><b>{stats.techs}</b><span>Tech falls</span></div>
              <div className="stat"><b>{stats.majors}</b><span>Majors</span></div>
              <div className="stat"><b>{stats.decisions}</b><span>Decisions</span></div>
              {stats.currentWar !== undefined && (
                <div className="stat stat-war">
                  <b>{stats.currentWar}</b>
                  <span>
                    WAR rating
                    {stats.warTrend !== undefined && stats.warTrend !== 0 && (
                      <em className={stats.warTrend > 0 ? "trend-up" : "trend-down"}>
                        {" "}{stats.warTrend > 0 ? "▲" : "▼"}{Math.abs(stats.warTrend)}
                      </em>
                    )}
                  </span>
                </div>
              )}
            </div>
            {selected?.club && <p className="muted">{selected.club}</p>}
          </div>

          {/* Wrestler-specific scouting report + real stat strip */}
          {scouting.data?.facts && scouting.data.facts.totalMatches > 0 && (
            <div className="card scouting-card">
              <div className="row spread">
                <h2 style={{ margin: 0 }}>
                  Scouting report
                  {selected ? ` · ${selected.displayName}` : ""}
                </h2>
                <span className="badge accent">AI · from real matches</span>
              </div>
              {scouting.loading && (
                <p className="muted">Breaking down the tape…</p>
              )}
              {scouting.data.blurb && (
                <p className="scouting-blurb">{scouting.data.blurb}</p>
              )}

              {(() => {
                const f = scouting.data.facts;
                return (
                  <>
                    <div className="stat-strip">
                      <div className="stat-chip">
                        <b>
                          {f.currentStreak > 0 && (
                            <span className="streak-flame">🔥 </span>
                          )}
                          {f.currentStreak > 0
                            ? `W${f.currentStreak}`
                            : f.currentStreak < 0
                              ? `L${-f.currentStreak}`
                              : "—"}
                        </b>
                        <span>streak</span>
                      </div>
                      <div className="stat-chip">
                        <b>{f.pinRate}%</b>
                        <span>pin rate</span>
                      </div>
                      <div className="stat-chip">
                        <b>{f.winPct}%</b>
                        <span>win rate</span>
                      </div>
                      <div className="stat-chip last5">
                        <span className="last5-dots">
                          {f.recentForm.map((r, i) => (
                            <span
                              key={i}
                              className={
                                "dot " +
                                (r === "W" ? "w" : r === "L" ? "l" : "d")
                              }
                              title={r}
                            />
                          ))}
                        </span>
                        <span>last {f.recentForm.length}</span>
                      </div>
                    </div>
                    <div className="scouting-facts">
                      {f.bestWin && (
                        <span>
                          <b>Best win:</b> {f.bestWin.opponent}
                          {f.bestWin.club ? ` (${f.bestWin.club})` : ""} ·{" "}
                          {f.bestWin.event}
                        </span>
                      )}
                      {f.topRival && (
                        <span>
                          <b>Rivalry:</b> {f.topRival.opponent}{" "}
                          {f.topRival.wins}-{f.topRival.losses}
                        </span>
                      )}
                      {f.milestones.map((ms) => (
                        <span key={ms} className="badge pin">
                          {ms}
                        </span>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* WAR rating trend + provider career stats */}
          {(warSeries.length > 1 || providerStats) && (
            <div className="card">
              {warSeries.length > 1 && (
                <div className="war-trend">
                  <div className="row spread">
                    <h2 style={{ margin: 0 }}>WAR rating trend</h2>
                    <span className="muted">
                      {warSeries[0]} → {warSeries[warSeries.length - 1]}
                    </span>
                  </div>
                  <Sparkline values={warSeries} width={320} height={56} />
                </div>
              )}

              {providerStats && (
                <div style={{ marginTop: warSeries.length > 1 ? "1rem" : 0 }}>
                  {(providerStats.careerWins !== undefined ||
                    providerStats.careerLosses !== undefined) && (
                    <p className="muted" style={{ margin: "0 0 0.5rem" }}>
                      Career (per Wrestling Reimagined):{" "}
                      <b>
                        {providerStats.careerWins ?? 0}–
                        {providerStats.careerLosses ?? 0}
                      </b>
                    </p>
                  )}
                  {(providerStats.resultTypes?.length ||
                    providerStats.scoringOptions?.length) && (
                    <div className="record-stats">
                      {providerStats.resultTypes?.map((r) => (
                        <div key={r.name} className="stat">
                          <b>{r.total}</b>
                          <span>{r.name}</span>
                        </div>
                      ))}
                      {providerStats.scoringOptions?.map((r) => (
                        <div key={r.name} className="stat">
                          <b>{r.total}</b>
                          <span>{r.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {providerStats.summaryMessage && (
                    <p className="war-summary">{providerStats.summaryMessage}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="card">
            <div className="row">
              {styles.length > 1 && (
                <select style={{ width: "auto" }} value={style} onChange={(e) => setStyle(e.target.value)}>
                  <option value="">All styles</option>
                  {styles.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              )}
              <select style={{ width: "auto" }} value={season} onChange={(e) => setSeason(e.target.value)}>
                <option value="">All years</option>
                {seasons.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select style={{ width: "auto" }} value={resultFilter} onChange={(e) => setResultFilter(e.target.value)}>
                <option value="">All results</option>
                <option value="W">Wins</option>
                <option value="L">Losses</option>
              </select>
            </div>
          </div>

          {/* Match history */}
          {filtered.length === 0 ? (
            <p className="muted">No matches match these filters.</p>
          ) : (
            <div className="stagger">
              {filtered.map((m) => (
                <div
                  key={m.matchId}
                  className={`card match-row match-${m.result.toLowerCase()}`}
                >
                  <div className="match-result-badge">{m.result}</div>
                  <div className="match-body">
                    <div className="row spread">
                      <strong>
                        vs {m.opponentName}
                        {m.opponentClub ? <span className="muted"> · {m.opponentClub}</span> : null}
                      </strong>
                      <span className="row" style={{ gap: "0.35rem" }}>
                        {m.winType && <span className="badge">{WIN_TYPE_LABEL[m.winType] ?? m.winType}</span>}
                        {typeof m.scoreFor === "number" && typeof m.scoreAgainst === "number" && (
                          <span className="badge">{m.scoreFor}–{m.scoreAgainst}</span>
                        )}
                      </span>
                    </div>
                    <p className="muted match-meta">
                      {fmtDate(m.eventDate)} · {m.eventName}
                      {m.round ? ` · ${m.round}` : ""}
                      {m.weightClass ? ` · ${m.weightClass} lb` : ""}
                      {m.style ? ` · ${m.style}` : ""}
                      {typeof m.warAfter === "number" &&
                        typeof m.warBefore === "number" && (
                          <span className={m.warAfter >= m.warBefore ? "trend-up" : "trend-down"}>
                            {" "}· WAR {m.warAfter >= m.warBefore ? "+" : ""}
                            {m.warAfter - m.warBefore}
                          </span>
                        )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
